import { useEffect, useState, useRef, useMemo } from 'react';
import { useTenant } from '../hooks/queries';
import {
  clearAllDeviceTokens,
  deleteCurrentDevice,
} from '../utils/deviceToken';

interface UserTenant {
  tenant_id: string;
  tenant_name: string;
  role: 'OWNER' | 'MANAGER' | 'STAFF';
}

/**
 * 導航列右上角的商家使用者選單
 * - 未登入時：顯示"未登入"按鈕，點擊直接觸發 Google OAuth
 * - 已登入時：顯示使用者資訊和下拉選單
 */
export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [userTenants, setUserTenants] = useState<UserTenant[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 監聽 localStorage 變化（用於處理 OAuth 回調後的 token 儲存和路由切換）
  useEffect(() => {
    const checkToken = () => {
      // 優先使用 tenant_token（用於查詢 userTenants 和 member 資訊）
      // 如果沒有 tenant_token，則使用 staff_session_token（PIN 碼登入）
      const currentToken =
        localStorage.getItem('tenant_token') ||
        localStorage.getItem('staff_session_token');
      setToken(prevToken => {
        // 只有當 token 真正改變時才更新，避免無限循環
        if (currentToken !== prevToken) {
          return currentToken;
        }
        return prevToken;
      });
    };

    // 立即檢查一次
    checkToken();

    // 監聽 storage 事件（跨標籤頁）
    window.addEventListener('storage', checkToken);

    // 監聽路由變化（當切換 tab 時重新檢查）
    const handleRouteChange = () => {
      checkToken();
    };
    window.addEventListener('popstate', handleRouteChange);

    // 監聽自定義事件（同標籤頁內的 localStorage 變化）
    window.addEventListener('localStorageChange', checkToken);

    // 定時檢查（用於處理 OAuth 回調後的狀態更新，每 500ms 檢查一次）
    const interval = setInterval(checkToken, 500);

    return () => {
      window.removeEventListener('storage', checkToken);
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('localStorageChange', checkToken);
      clearInterval(interval);
    };
  }, []); // 移除 token 依賴，使用函數式更新避免無限循環

  // 使用 state 來存儲 tenant_id，以便在變化時觸發重新查詢
  // 初始值從 localStorage 讀取，確保組件掛載時就有正確的值
  const [tenantId, setTenantId] = useState<string | null>(() => {
    return localStorage.getItem('tenant_id');
  });

  // 監聽 tenant_id 變化
  useEffect(() => {
    const checkTenantId = () => {
      const currentTenantId = localStorage.getItem('tenant_id');
      setTenantId(prevTenantId => {
        if (currentTenantId !== prevTenantId) {
          return currentTenantId;
        }
        return prevTenantId;
      });
    };

    // 立即檢查一次（確保與 localStorage 同步）
    checkTenantId();

    // 監聽 storage 事件（跨標籤頁）
    window.addEventListener('storage', checkTenantId);

    // 監聽自定義事件（同標籤頁內的 localStorage 變化）
    window.addEventListener('localStorageChange', checkTenantId);

    // 定時檢查（用於處理 tenant_id 設置後的狀態更新）
    const interval = setInterval(checkTenantId, 500);

    return () => {
      window.removeEventListener('storage', checkTenantId);
      window.removeEventListener('localStorageChange', checkTenantId);
      clearInterval(interval);
    };
  }, []);

  // 檢查是否是 staff session（PIN 碼登入）
  // 注意：由於 Phase 1.1 已確保 token 互斥，這裡只需要檢查 staff_session_token 是否存在
  const isStaffSession = useMemo(() => {
    return !!localStorage.getItem('staff_session_token');
  }, [token]);

  // 使用 useMemo 確保 fetch 選項在 tenantId 變化時更新
  // 只有在 token 和 tenantId 都存在時才創建 fetchOptions
  const fetchOptions = useMemo(() => {
    if (!tenantId) return undefined;
    // Staff session 時使用 staff_session_token，member session 時使用 tenant_token
    const staffToken = localStorage.getItem('staff_session_token');
    const tenantToken = localStorage.getItem('tenant_token');
    const authToken = tenantToken || staffToken;

    if (!authToken) return undefined;

    return {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-Tenant-ID': tenantId, // tenantId 已確保存在
      },
    } as RequestInit;
  }, [token, tenantId, isStaffSession]);

  // Staff session 時也應該嘗試調用 /api/tenants/me（後端應該允許 staff 查詢基本資訊）
  // 只要有 tenantId 和 token（無論是 staff_session_token 還是 tenant_token）就嘗試查詢
  const shouldFetch = !!tenantId && !!fetchOptions;

  // 使用 custom hook 來獲取商家資訊
  const {
    tenant,
    isLoading,
    mutate: mutateTenant,
  } = useTenant(
    shouldFetch && fetchOptions
      ? {
          fetch: fetchOptions,
          enabled: shouldFetch,
          revalidateOnFocus: false,
          dedupingInterval: 600000, // 10 分鐘
        }
      : {
          enabled: false,
        }
  );

  // 載入用戶所屬的所有租戶
  // 注意：此 API 需要 tenant_token（member token），staff_session_token 無法使用
  // 但如果用戶同時有 tenant_token，則應該載入 userTenants 以便檢查是否同時是 member
  useEffect(() => {
    const loadUserTenants = async () => {
      // 只使用 tenant_token 來查詢 userTenants（此 API 需要 member 權限）
      const tenantToken = localStorage.getItem('tenant_token');
      if (!tenantToken) {
        setUserTenants([]);
        return;
      }

      try {
        const apiBase =
          import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
        const response = await fetch(`${apiBase}/api/auth/user/tenants`, {
          headers: {
            Authorization: `Bearer ${tenantToken}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && result.data.tenants) {
            setUserTenants(result.data.tenants);
          }
        }
      } catch (error) {
        console.error('載入用戶租戶列表失敗:', error);
      }
    };

    loadUserTenants();
  }, [token]); // 當 token 變化時重新載入（可能是從 staff_session_token 切換到 tenant_token）

  // 當 token 或 tenant_id 變化時，強制重新查詢
  const prevTokenRef = useRef<string | null>(null);
  const prevTenantIdRef = useRef<string | null>(null);
  useEffect(() => {
    // 只在 token 或 tenant_id 真正變化時才重新查詢
    const tokenChanged = token && token !== prevTokenRef.current;
    const tenantIdChanged = tenantId !== prevTenantIdRef.current;

    if (tokenChanged || tenantIdChanged) {
      if (token) {
        prevTokenRef.current = token;
      }
      if (tenantId) {
        prevTenantIdRef.current = tenantId;
      }

      // Token 或 tenant_id 變化時，使用 mutate 重新驗證
      mutateTenant();
    } else if (!token) {
      prevTokenRef.current = null;
      prevTenantIdRef.current = null;
    }
  }, [token, tenantId, mutateTenant]);

  // 載入當前用戶的身份資訊（姓名和郵箱）
  const [currentUserInfo, setCurrentUserInfo] = useState<{
    name: string | null;
    email: string | null;
  } | null>(null);

  useEffect(() => {
    const loadCurrentUserInfo = async () => {
      // 檢查當前路由是否為 tenant-select（lobby 頁面）
      // 在 lobby 頁面時，不應該調用 API（因為還沒有選擇 tenant）
      const currentPath = window.location.pathname;
      const isLobbyPage =
        currentPath === '/tenant-select' ||
        currentPath.includes('/tenant-select');

      if (!token || !tenantId || isLobbyPage) {
        setCurrentUserInfo(null);
        return;
      }

      // 檢查是否是 staff session
      const isStaffSession =
        !!localStorage.getItem('staff_session_token') &&
        !localStorage.getItem('tenant_token');

      if (isStaffSession) {
        // 如果是 staff session，從 localStorage 獲取 staff 資訊
        const staffName = localStorage.getItem('staff_name');
        if (staffName) {
          setCurrentUserInfo({
            name: staffName,
            email: null,
          });
        }
        return;
      }

      // 否則，嘗試從 API 獲取 member 資訊
      try {
        const apiBase =
          import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
        const response = await fetch(`${apiBase}/api/members/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': tenantId,
          },
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[UserMenu] 取得用戶資訊:', result);
          if (result.success && result.data) {
            const userInfo = {
              name: result.data.user_name || null,
              email: result.data.user_email || null,
            };
            console.log('[UserMenu] 設置用戶資訊:', userInfo);
            setCurrentUserInfo(userInfo);
          } else {
            console.warn('[UserMenu] 響應格式不正確:', result);
          }
        } else {
          // 403 或 404 錯誤可能是 staff session 沒有 member 權限，或是在 lobby 頁面，這是正常的
          if (response.status !== 403 && response.status !== 404) {
            console.error(
              '[UserMenu] 取得用戶資訊失敗:',
              response.status,
              response.statusText
            );
          }
        }
      } catch (error) {
        // 在 lobby 頁面或沒有 tenant 時，不應該記錄錯誤
        if (!isLobbyPage && tenantId) {
          console.error('載入當前用戶資訊失敗:', error);
        }
      }
    };

    loadCurrentUserInfo();
  }, [token, tenantId]);

  // 處理錯誤：清除無效的 token
  // 注意：SWR 的錯誤處理在 fetcher 中統一處理，這裡只需要處理特定情況
  // 如果是 staff_session_token，可能只是沒有 member 權限，但 staff 權限正常

  // 已登入時：顯示使用者資訊和下拉選單
  // 注意：這些計算必須在條件返回之前，因為後面的 hooks 需要用到
  const currentTenantId = localStorage.getItem('tenant_id');
  const currentTenant = userTenants.find(t => t.tenant_id === currentTenantId);

  // 檢查 staff 是否同時是 member（如果 currentTenantId 在 userTenants 中，則表示同時是 member）
  const isStaffAlsoMember =
    isStaffSession &&
    currentTenantId &&
    userTenants.some(t => t.tenant_id === currentTenantId);

  // 如果是 staff session 但同時也是 member，可以顯示 member 相關的選項（如離開商店）
  // 如果不是 staff session，則顯示所有 member 選項
  const canShowMemberOptions = !isStaffSession || isStaffAlsoMember;

  // 當獲取到 tenant 名稱時，緩存到 localStorage（即使登出後也能顯示）
  // 必須在條件返回之前調用所有 hooks
  useEffect(() => {
    if (tenant?.name && currentTenantId) {
      localStorage.setItem(`tenant_name_${currentTenantId}`, tenant.name);
    } else if (currentTenant?.tenant_name && currentTenantId) {
      localStorage.setItem(
        `tenant_name_${currentTenantId}`,
        currentTenant.tenant_name
      );
    }
  }, [tenant?.name, currentTenant?.tenant_name, currentTenantId]);

  // 嘗試從 localStorage 讀取緩存的 tenant 名稱（登出後使用）
  const cachedTenantName = useMemo(() => {
    if (currentTenantId) {
      return localStorage.getItem(`tenant_name_${currentTenantId}`);
    }
    return null;
  }, [currentTenantId]);

  // 處理滑鼠移入：清除關閉計時器
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // 處理滑鼠移出：延遲關閉選單
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms 延遲，讓用戶有時間移動滑鼠到選單外部
  };

  // 移除點擊外部立即關閉的邏輯，改為只在滑鼠移出時延遲關閉
  // 這樣用戶可以點擊選單內的項目而不會立即關閉

  // 清理 timeout（當組件卸載或 isOpen 變化時）
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen]);

  // 判斷是否未登入的邏輯（使用 useMemo 和 Guard Clauses 提高可讀性）
  // 邏輯順序：
  // 1. 沒有 token → 未登入
  // 2. 是 staff session → 已登入（即使 API 返回 403）
  // 3. API 返回 401/404 → 未登入
  // 4. 正在載入 → 暫時視為已登入（避免閃爍）
  // 5. 有 token 但查詢失敗且沒有 tenant 資料 → 未登入
  const isUnauthenticated = useMemo(() => {
    // Guard 1: 如果沒有任何 token，視為未登入
    if (!token) return true;

    // Guard 2: 如果是 staff session，視為已登入（即使 API 返回 403）
    if (isStaffSession) return false;

    // Guard 3: 如果 API 還在載入中，暫時視為已登入（避免閃爍）
    if (isLoading) return false;

    // Guard 4: 如果有 token 但查詢失敗且沒有 tenant 資料，視為未登入
    if (token && tenantId && !tenant) return true;

    // 其他情況視為已登入
    return false;
  }, [token, isStaffSession, isLoading, tenantId, tenant]);

  if (isUnauthenticated) {
    const handleLogin = () => {
      const apiBaseUrl =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const redirectUri = `${window.location.origin}/tenant-register`;
      window.location.href = `${apiBaseUrl}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`;
    };

    return (
      <div className="ml-auto">
        <button
          type="button"
          onClick={handleLogin}
          className="px-4 py-2 rounded-md text-sm font-medium transition-colors text-gray-300 hover:bg-gray-700 hover:text-white"
        >
          登入
        </button>
      </div>
    );
  }

  // 按鈕顯示使用者名稱（優先使用 name，如果沒有則使用 email）
  const userName =
    currentUserInfo?.name ||
    currentUserInfo?.email ||
    (isStaffSession ? localStorage.getItem('staff_name') || '員工' : '使用者');
  // 顯示名稱：優先使用實際的 tenant 名稱，如果沒有則使用緩存的名稱
  const displayName =
    tenant?.name ||
    currentTenant?.tenant_name ||
    cachedTenantName ||
    (isStaffSession ? 'POS 系統' : '商家');

  // 頭像縮寫使用使用者名稱的第一個字符
  const initials =
    userName && userName.trim().length > 0
      ? userName.trim().charAt(0).toUpperCase()
      : '用';

  // 登出：清除所有 session token，但保留 tenant_id（可用 PIN code 登入或快速重新登入）
  // 根據登入類型區分處理：
  // - Staff 登出: 清除 staff 相關 token，保留 tenant_id 和 device_token（可用 PIN code 重新登入）
  // - Member 登出: 清除所有 session token，保留 tenant_id（便於快速重新登入，提升 UX）
  const handleLogout = () => {
    setIsOpen(false);

    // 判斷當前是 Staff session 還是 Member session
    const isStaff = isStaffSession;

    if (isStaff) {
      // Staff 登出：清除 staff 相關 token 和 tenant_token，保留 tenant_id 和 device_token
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_id');
      localStorage.removeItem('staff_name');
      localStorage.removeItem('tenant_token'); // 確保清除任何殘留的 tenant_token
      // 保留 tenant_id 和 device_token（使用 Helper），這樣可以用 PIN code 重新登入
    } else {
      // Member 登出：清除所有 session token，保留 tenant_id
      localStorage.removeItem('tenant_token');
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_id');
      localStorage.removeItem('staff_name');
      // 保留 tenant_id（便於快速重新登入，提升 UX）
      // 注意：Member 登出時不保留 device_token，因為 Member 不使用 PIN code 登入
    }

    window.dispatchEvent(new Event('localStorageChange'));
    // 導向 POS 頁面（可以用 PIN code 登入或 Google OAuth 重新登入）
    window.location.href = '/pos';
  };

  // 離開商店：清除所有 tenant 相關資訊，回到公用區域（選擇商店頁面）
  const handleLeaveTenant = async () => {
    setIsOpen(false);

    // 先刪除當前裝置（如果有 tenant_token 和 device_token）
    const tenantToken = localStorage.getItem('tenant_token');
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantToken && tenantId) {
      try {
        await deleteCurrentDevice(tenantId, tenantToken);
      } catch (error) {
        console.error('刪除裝置時發生錯誤:', error);
        // 即使刪除失敗，也繼續清除本地資訊
      }
    }

    // 清除所有 tenant 和 session 相關的資訊
    localStorage.removeItem('tenant_token');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('staff_session_token');
    localStorage.removeItem('staff_id');
    localStorage.removeItem('staff_name');
    // 清除所有租戶的 device_token（使用 Helper 確保完全清除）
    clearAllDeviceTokens();
    window.dispatchEvent(new Event('localStorageChange'));
    // 導向選擇商店頁面（公用區域）
    window.location.href = '/tenant-select';
  };

  const handleSwitchTenant = (tenantId: string) => {
    setIsOpen(false);
    localStorage.setItem('tenant_id', tenantId);
    window.dispatchEvent(new Event('localStorageChange'));
    // 重新載入頁面以更新所有組件
    window.location.reload();
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER':
        return '擁有者';
      case 'MANAGER':
        return '管理員';
      case 'STAFF':
        return '工作人員';
      default:
        return role;
    }
  };

  return (
    <div
      ref={menuRef}
      className="ml-auto relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-2 rounded-full bg-gray-700 hover:bg-gray-600 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500"
      >
        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
          {initials}
        </div>
        {!isLoading && currentUserInfo && (
          <span className="hidden md:inline text-xs text-gray-200 truncate max-w-[160px]">
            {userName}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-lg bg-white py-2 shadow-lg ring-1 ring-black/5 z-50"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="px-4 pb-2 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {displayName}
            </p>
            <div className="mt-1 space-y-0.5">
              {currentUserInfo?.name && (
                <p className="text-xs text-gray-900 truncate font-medium">
                  {currentUserInfo.name}
                </p>
              )}
              {currentUserInfo?.email && (
                <p className="text-xs text-gray-600 truncate">
                  {currentUserInfo.email}
                </p>
              )}
              {currentTenant && (
                <p className="text-xs text-gray-500 truncate">
                  {getRoleLabel(currentTenant.role)}
                </p>
              )}
            </div>
          </div>

          {/* 多租戶切換（只有 member 權限才顯示） */}
          {canShowMemberOptions && userTenants.length > 1 && (
            <>
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-2">
                  切換商家
                </p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userTenants.map(t => (
                    <button
                      key={t.tenant_id}
                      type="button"
                      onClick={() => handleSwitchTenant(t.tenant_id)}
                      className={`w-full px-3 py-1.5 text-left text-xs rounded hover:bg-gray-100 transition-colors ${
                        t.tenant_id === currentTenantId
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">{t.tenant_name}</span>
                        <span className="ml-2 text-xs text-gray-500">
                          {getRoleLabel(t.role)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* 只有 member 權限（currentTenant 存在）或 staff 同時是 member 時才顯示「離開商店」選項 */}
          {(currentTenant || isStaffAlsoMember) && (
            <button
              type="button"
              onClick={handleLeaveTenant}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
            >
              離開商店
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
          >
            登出
          </button>
        </div>
      )}
    </div>
  );
}
